<template>
  <div class="notification-badge">
    <button 
      class="btn btn-link notification-btn position-relative p-0" 
      @click="toggleNotifications"
    >
      <i class="bi bi-bell-fill fs-5"></i>
      <span 
        v-if="unreadCount > 0" 
        class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
        <span class="visually-hidden">unread notifications</span>
      </span>
    </button>
    
    <div 
      v-if="showDropdown" 
      class="notification-dropdown shadow-sm"
      ref="dropdown"
    >
      <div class="notification-header d-flex justify-content-between align-items-center">
        <h6 class="m-0">Notifications</h6>
        <div>
          <button 
            v-if="hasUnread" 
            class="btn btn-sm btn-link text-decoration-none" 
            @click="markAllAsRead"
            :disabled="markingRead"
          >
            <small>Mark all as read</small>
          </button>
          <button 
            class="btn btn-sm btn-link text-decoration-none ms-2"
            @click="closeDropdown"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
      
      <div class="notification-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted small mt-2">Loading notifications...</p>
        </div>
        
        <div v-else-if="!notifications.length" class="text-center py-4">
          <i class="bi bi-bell-slash text-muted fs-4"></i>
          <p class="text-muted small mt-2">No notifications</p>
        </div>
        
        <template v-else>
          <div 
            v-for="notification in notifications" 
            :key="notification.id"
            class="notification-item" 
            :class="{ 'unread': !notification.is_read }"
            @click="viewNotification(notification)"
          >
            <div class="notification-icon">
              <i :class="getNotificationIcon(notification.notification_type)"></i>
            </div>
            <div class="notification-content">
              <div class="notification-title">{{ notification.title }}</div>
              <div class="notification-meta">
                <span class="notification-time">{{ formatTime(notification.created_at) }}</span>
              </div>
              <div class="notification-message">{{ truncateMessage(notification.message) }}</div>
            </div>
            <div class="notification-actions">
              <button 
                v-if="!notification.is_read"
                class="btn btn-sm btn-link p-0 me-2" 
                title="Mark as read"
                @click.stop="markAsRead(notification)"
              >
                <i class="bi bi-check-circle"></i>
              </button>
            </div>
          </div>
        </template>
      </div>
      
      <div class="notification-footer">
        <a href="#" @click.prevent="viewAllNotifications" class="view-all">View all notifications</a>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'NotificationBadge',
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      showDropdown: false,
      loading: false,
      markingRead: false,
      refreshInterval: null
    }
  },
  computed: {
    hasUnread() {
      return this.notifications.some(notification => !notification.is_read);
    }
  },
  mounted() {
    // Get initial notification count
    this.fetchUnreadCount();
    
    // Set up auto-refresh every 2 minutes
    this.refreshInterval = setInterval(() => {
      this.fetchUnreadCount();
    }, 120000); // 2 minutes
    
    // Add click outside handler
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    // Clean up interval and event listeners
    clearInterval(this.refreshInterval);
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async fetchUnreadCount() {
      try {
        const response = await api.getUnreadCounts();
        this.unreadCount = response.data.unread_notifications || 0;
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    },
    
    async fetchNotifications() {
      this.loading = true;
      
      try {
        const response = await api.getNotifications();
        // Sort notifications by creation date (newest first) and limit to 10
        this.notifications = response.data.slice(0, 10);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },
    
    async markAsRead(notification) {
      try {
        await api.markNotificationAsRead(notification.id);
        
        // Update the notification in the list
        const index = this.notifications.findIndex(n => n.id === notification.id);
        if (index !== -1) {
          this.notifications[index].is_read = true;
        }
        
        // Update unread count
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    
    async markAllAsRead() {
      if (this.markingRead) return;
      
      this.markingRead = true;
      
      try {
        await api.markAllNotificationsAsRead();
        
        // Update all notifications in the list
        this.notifications.forEach(notification => {
          notification.is_read = true;
        });
        
        // Reset unread count
        this.unreadCount = 0;
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
      } finally {
        this.markingRead = false;
      }
    },
    
    toggleNotifications() {
      this.showDropdown = !this.showDropdown;
      
      if (this.showDropdown) {
        this.fetchNotifications();
      }
    },
    
    closeDropdown() {
      this.showDropdown = false;
    },
    
    handleClickOutside(event) {
      const dropdown = this.$refs.dropdown;
      const button = this.$el.querySelector('.notification-btn');
      
      if (this.showDropdown && dropdown && !dropdown.contains(event.target) && !button.contains(event.target)) {
        this.closeDropdown();
      }
    },
    
    viewNotification(notification) {
      // Mark as read if needed
      if (!notification.is_read) {
        this.markAsRead(notification);
      }
      
      // Add custom logic here to view the notification details
      // For example, open a modal or navigate to a details page
      
      // Close dropdown
      this.closeDropdown();
    },
    
    viewAllNotifications() {
      // Navigate to notifications page
      // If you're an admin, go to admin notifications, otherwise to user notifications
      if (this.isAdmin()) {
        this.$router.push('/admin?section=notifications');
      } else {
        this.$router.push('/notifications');
      }
      
      this.closeDropdown();
    },
    
    isAdmin() {
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const role = userData.role_details?.name || 
                    (userData.role && typeof userData.role === 'object' ? userData.role.name : userData.role);
        
        return role === 'admin';
      } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
    },
    
    // Format the time (e.g., "2 hours ago", "Yesterday", etc.)
    formatTime(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffSecs < 60) {
        return 'Just now';
      } else if (diffMins < 60) {
        return `${diffMins} min ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hr ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString();
      }
    },
    
    // Get appropriate icon for notification type
    getNotificationIcon(type) {
      switch (type) {
        case 'exam_started':
          return 'bi bi-pencil-square text-primary';
        case 'exam_completed':
          return 'bi bi-check-circle text-success';
        case 'course_started':
          return 'bi bi-book text-info';
        case 'user_registered':
          return 'bi bi-person-plus text-secondary';
        case 'payment_received':
          return 'bi bi-credit-card text-warning';
        case 'system':
          return 'bi bi-gear text-dark';
        default:
          return 'bi bi-bell text-secondary';
      }
    },
    
    // Truncate message text for preview
    truncateMessage(message) {
      if (!message) return '';
      
      const maxLength = 60;
      
      if (message.length <= maxLength) {
        return message;
      }
      
      return message.substring(0, maxLength) + '...';
    }
  }
}
</script>

<style scoped lang="scss">
.notification-badge {
  position: relative;
  
  .notification-btn {
    color: #444;
    padding: 10px;
    
    &:hover {
      color: #000;
    }
  }
  
  .notification-dropdown {
    position: absolute;
    top: calc(100% + 5px);
    right: 0;
    width: 320px;
    max-height: 500px;
    background-color: #fff;
    border-radius: 8px;
    z-index: 1000;
    overflow: hidden;
    
    .notification-header {
      padding: 12px 15px;
      border-bottom: 1px solid #eee;
      
      h6 {
        font-size: 16px;
        font-weight: 600;
      }
      
      .btn-link {
        color: #0d6efd;
        padding: 0;
        
        &:hover {
          color: #0a58ca;
        }
      }
    }
    
    .notification-body {
      max-height: 380px;
      overflow-y: auto;
      
      .notification-item {
        padding: 12px 15px;
        border-bottom: 1px solid #f5f5f5;
        display: flex;
        align-items: flex-start;
        cursor: pointer;
        transition: background-color 0.2s;
        
        &:hover {
          background-color: #f8f9fa;
        }
        
        &.unread {
          background-color: #f0f7ff;
          
          &:hover {
            background-color: #e5f1ff;
          }
          
          .notification-title {
            font-weight: 600;
          }
        }
        
        .notification-icon {
          flex-shrink: 0;
          margin-right: 12px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          
          i {
            font-size: 16px;
          }
        }
        
        .notification-content {
          flex: 1;
          min-width: 0;
          
          .notification-title {
            font-size: 14px;
            margin-bottom: 3px;
            line-height: 1.3;
          }
          
          .notification-meta {
            font-size: 12px;
            color: #6c757d;
            margin-bottom: 3px;
          }
          
          .notification-message {
            font-size: 13px;
            color: #495057;
            line-height: 1.4;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
        }
        
        .notification-actions {
          margin-left: 8px;
          
          .btn-link {
            color: #adb5bd;
            
            &:hover {
              color: #0d6efd;
            }
          }
        }
      }
    }
    
    .notification-footer {
      padding: 12px 15px;
      text-align: center;
      border-top: 1px solid #eee;
      
      .view-all {
        color: #0d6efd;
        font-size: 14px;
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 575.98px) {
  .notification-dropdown {
    width: 280px;
    right: -70px;
    
    &:before {
      right: 80px;
    }
  }
}
</style>